import React, { useState } from 'react';
import { IRole } from '@/types/roles-permissions';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet-async';
import { RolesService } from '@/services/roles/rolesService';
import {
  RolesList,
  AddRoleModal,
  EditRoleModal,
  DeleteRoleModal,
} from './components';

const RolesManagementPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false);
  const [isCreatingRole, setIsCreatingRole] = useState(false);
  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [selectedRole, setSelectedRole] = useState<IRole | null>(null);
  const [isDeletingRole, setIsDeletingRole] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<IRole | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleEditRole = (role: IRole) => {
    setSelectedRole(role);
    setIsEditRoleModalOpen(true);
  };

  const handleDeleteRole = (role: IRole) => {
    setRoleToDelete(role);
    setIsDeleteModalOpen(true);
  };

  const handleOpenAddRoleModal = () => {
    setIsAddRoleModalOpen(true);
  };

  const handleCloseAddRoleModal = () => {
    setIsAddRoleModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditRoleModalOpen(false);
    setSelectedRole(null);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setRoleToDelete(null);
  };

  const handleCreateRole = async (roleData: {
    name: string;
    has_admin_panel_access: boolean;
    has_user_front_access: boolean;
    description: string;
  }) => {
    if (!roleData.name.trim()) {
      enqueueSnackbar('Role name is required', {
        variant: 'error',
      });
      return;
    }

    try {
      setIsCreatingRole(true);
      await RolesService.createRole(roleData);
      enqueueSnackbar('Role created successfully', { variant: 'success' });
      handleCloseAddRoleModal();

      setRefreshTrigger((prev) => prev + 1);
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.message || err?.message || 'Failed to create role';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsCreatingRole(false);
    }
  };

  const handleUpdateRole = async (
    roleId: string,
    roleData: {
      name: string;
      has_admin_panel_access: boolean;
      has_user_front_access: boolean;
      description: string;
    }
  ) => {
    if (!roleData.name.trim()) {
      enqueueSnackbar('Role name is required', { variant: 'error' });
      return;
    }

    try {
      setIsUpdatingRole(true);
      await RolesService.updateRole(roleId, roleData);
      enqueueSnackbar('Role updated successfully', { variant: 'success' });
      handleCloseEditModal();

      setRefreshTrigger((prev) => prev + 1);
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.message || err?.message || 'Failed to update role';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!roleToDelete) return;

    try {
      setIsDeletingRole(true);
      await RolesService.deleteRole(roleToDelete.id);
      enqueueSnackbar('Role deleted successfully', { variant: 'success' });
      setIsDeleteModalOpen(false);
      setRoleToDelete(null);

      setRefreshTrigger((prev) => prev + 1);
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.message || err?.message || 'Failed to delete role';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsDeletingRole(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Roles and Permissions Management</title>
      </Helmet>
      <div className="container-fixed mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl font-medium leading-none text-gray-900 dark:text-white">
            Roles and Permissions
          </h1>
          <button
            className="btn btn-sm btn-light dark:bg-coal-300 dark:text-white dark:hover:bg-coal-400 dark:border-coal-100"
            onClick={handleOpenAddRoleModal}
          >
            Add New Role
          </button>
        </div>

        <RolesList
          onEditRole={handleEditRole}
          onDeleteRole={handleDeleteRole}
          refreshTrigger={refreshTrigger}
        />
      </div>

      <AddRoleModal
        isOpen={isAddRoleModalOpen}
        isCreating={isCreatingRole}
        onClose={handleCloseAddRoleModal}
        onCreateRole={handleCreateRole}
      />

      <DeleteRoleModal
        isOpen={isDeleteModalOpen}
        isDeleting={isDeletingRole}
        role={roleToDelete}
        onClose={handleCancelDelete}
        onConfirmDelete={handleConfirmDelete}
      />

      <EditRoleModal
        isOpen={isEditRoleModalOpen}
        isUpdating={isUpdatingRole}
        role={selectedRole}
        onClose={handleCloseEditModal}
        onUpdateRole={handleUpdateRole}
      />
    </>
  );
};

export default RolesManagementPage;
